<script setup lang="ts">
import type { ExclusivePropertiesSectionTitleVariants } from '.';
import type { Carousel } from '@/components/ui/carousel';

import { type Property } from '@/core/entities/property';
import { NuxtLink } from '#components';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { GraphicsTriangle } from '~/components/GraphicsTriangle';

import { titleVariants } from '.';

withDefaults(
	defineProps<{
		properties: Property[];
		isLoading: boolean;
		titleVariant?: ExclusivePropertiesSectionTitleVariants;
	}>(),
	{
		titleVariant: () => 'default'
	}
);

// Constants
const SLIDE_DELAY = 2000;

// Refs and States
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);

const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: true
});
</script>
<template>
	<section class="relative min-h-[520px] w-full bg-primary md:min-h-[620px]">
		<LazyContainer class="px-0 py-[44px] sm:pb-[60px] sm:pt-[86px]">
			<header class="flex flex-col items-start px-5 pb-9">
				<LazySkeleton
					:is-active="isLoading"
					variant="primary"
					:class="isLoading ? 'mb-3 h-[34px] w-1/2 md:w-[305px]' : ''"
				>
					<h2
						:class="
							titleVariants({
								variant: titleVariant ?? 'default'
							})
						"
					>
						<GraphicsTriangle class="mr-6 w-[20px] text-beige md:w-[28px]" />

						Exclusivos
						<span class="ml-2 font-light md:font-normal">PilarHomes</span>
					</h2>
				</LazySkeleton>
				<LazySkeleton
					:is-active="isLoading"
					variant="primary"
					:class="isLoading ? 'h-[22px] w-3/4 lg:w-[856px]' : ''"
				>
					<p
						class="text-left font-sans text-sm font-light text-white md:text-[20px]"
					>
						Imóveis únicos, listados apenas no PilarHomes.
					</p>
				</LazySkeleton>
			</header>

			<LazyCarousel
				ref="carouselContainerRef"
				class="w-full"
				:plugins="[WheelGesturesPlugin()]"
				:opts="{ align: 'center' }"
			>
				<LazyCarouselContent class="gap-6 pl-5 md:pl-0">
					<template v-if="isLoading">
						<LazyCarouselItem
							v-for="i in 6"
							:key="i"
							class="h-[345px] max-w-[335px] last:pr-5 md:h-[380px] md:max-w-[464px]"
						>
							<PropertyCardSkeleton variant="transparentBackground" />
						</LazyCarouselItem>
					</template>
					<template v-else>
						<LazyCarouselItem
							v-for="(property, index) in properties"
							:key="property.commercialId"
							class="h-[345px] max-w-[335px] basis-11/12 last:pr-5 md:h-[380px] md:max-w-[464px] md:last:pr-0"
						>
							<LazyPropertyCard
								variant="transparentBackground"
								:property="property"
								:card-position="index"
								section="exclusives"
							/>
						</LazyCarouselItem>
					</template>
				</LazyCarouselContent>
				<div
					class="mt-6 flex h-auto w-full flex-col-reverse items-start gap-5 px-5 sm:left-auto sm:mt-9 sm:flex-row sm:justify-between md:mx-0 md:px-0"
				>
					<NuxtLink
						v-slot="{ href }"
						:to="
							PropertyFiltersPresenter.getRouteObjectFromFilters({
								states: ['SP'],
								cities: ['São Paulo'],
								isExclusive: true
							})
						"
						custom
					>
						<LazyButton
							as="a"
							:href="href"
							variant="beige"
							class="h-[48px] w-[146px] self-start sm:h-[52px] md:w-[172px]"
							:is-skeleton-loading="isLoading"
							skeleton-classes="w-[146px] md:w-[172px] h-[48px] sm:h-[52px]"
							skeleton-variant="primary"
						>
							Ver todos <IconMaterialSymbolsNorthEast class="w-5 md:w-6" />
						</LazyButton>
					</NuxtLink>
					<div class="flex w-full items-center gap-5">
						<div
							ref="carouselItemSelectorContainerRef"
							class="flex w-full flex-wrap justify-end gap-3"
						>
							<LazyCarouselItemSelector
								v-for="(_, index) in scrollableSlidesNumber"
								:key="`selector-${index}`"
								:active="currentCarouselItemIndex === index"
								:delay="SLIDE_DELAY"
								:dynamic-width="carouselDynamicItemWidth"
								@click="scrollTo(index)"
							/>
						</div>
						<div class="flex gap-1">
							<LazyCarouselGenericAction
								@click="scrollTo(currentCarouselItemIndex - 1)"
							>
								<IconMaterialSymbolsLightChevronLeft
									width="28px"
									height="28px"
								/>
							</LazyCarouselGenericAction>
							<LazyCarouselGenericAction>
								<IconMaterialSymbolsLightChevronRight
									width="28px"
									height="28px"
									@click="scrollTo(currentCarouselItemIndex + 1)"
								/>
							</LazyCarouselGenericAction>
						</div>
					</div>
				</div>
			</LazyCarousel>
		</LazyContainer>
	</section>
</template>
